import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Menu } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const Dashboard = ({ auth }) => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [commentOpen, setCommentOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [uploadData, setUploadData] = useState({
    title: '',
    description: '',
    category_id: '',
    doc_type: 'link',
    url: '',
    file: null,
    is_private: false
  });
  const [comment, setComment] = useState('');
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewingDoc, setViewingDoc] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [editingDoc, setEditingDoc] = useState(null);
  const [editDocOpen, setEditDocOpen] = useState(false);
  const [editDocData, setEditDocData] = useState({ title: '', description: '', category_id: '' });
  const [staff, setStaff] = useState([]);
  const [tradesmen, setTradesmen] = useState([]);
  const [otherContacts, setOtherContacts] = useState([]);
  const [showDirectory, setShowDirectory] = useState(false);
  const [staffDialogOpen, setStaffDialogOpen] = useState(false);
  const [tradesmanDialogOpen, setTradesmanDialogOpen] = useState(false);
  const [otherContactDialogOpen, setOtherContactDialogOpen] = useState(false);
  const [staffData, setStaffData] = useState({ full_name: '', phone: '', email: '' });
  const [tradesmanData, setTradesmanData] = useState({ business_name: '', trade_type: '', contact_number: '', email: '' });
  const [otherContactData, setOtherContactData] = useState({ business_name: '', what_they_do: '', name: '', contact_number: '', email: '' });
  const [deleteDocOpen, setDeleteDocOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [showPrivate, setShowPrivate] = useState(false);
  const [privateDocuments, setPrivateDocuments] = useState([]);
  const [docToDelete, setDocToDelete] = useState(null);
  const [replaceDocOpen, setReplaceDocOpen] = useState(false);
  const [docToReplace, setDocToReplace] = useState(null);
  const [replaceFile, setReplaceFile] = useState(null);

  useEffect(() => {
    fetchData();
    if (auth.user?.role === 'admin') {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 30000); // Check every 30 seconds
      return () => clearInterval(interval);
    }
  }, []);

  const fetchData = async () => {
    try {
      const [docsRes, catsRes, staffRes, tradesmenRes, contactsRes, privateDocsRes] = await Promise.all([
        axios.get(`${API}/documents`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/directory/staff`),
        axios.get(`${API}/directory/tradesmen`),
        axios.get(`${API}/directory/other-contacts`),
        axios.get(`${API}/documents/private`)
      ]);
      setDocuments(docsRes.data);
      setCategories(catsRes.data);
      setStaff(staffRes.data);
      setTradesmen(tradesmenRes.data);
      setOtherContacts(contactsRes.data);
      setPrivateDocuments(privateDocsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/notifications/unread/count`);
      setUnreadCount(response.data.count);
    } catch (error) {
      // Silently ignore — unread count is non-critical UI
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append('title', uploadData.title);
    formData.append('description', uploadData.description);
    formData.append('category_id', uploadData.category_id);
    formData.append('doc_type', uploadData.doc_type);
    formData.append('is_private', uploadData.is_private);

    if (uploadData.doc_type === 'link' || uploadData.doc_type === 'external_link') {
      formData.append('url', uploadData.url);
    } else if (uploadData.file) {
      formData.append('file', uploadData.file);
    }

    try {
      await axios.post(`${API}/documents/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (uploadData.is_private) {
        toast.success('Private document uploaded!');
      } else {
        toast.success(auth.user.role === 'admin' ? 'Document uploaded!' : 'Document submitted for approval');
      }
      
      setUploadOpen(false);
      setUploadData({ title: '', description: '', category_id: '', doc_type: 'link', url: '', file: null, is_private: false });
      fetchData();
    } catch (error) {
      toast.error('Upload failed');
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/comments`, {
        document_id: selectedDoc.id,
        message: comment
      });
      toast.success('Comment sent to admin');
      setCommentOpen(false);
      setComment('');
      setSelectedDoc(null);
    } catch (error) {
      toast.error('Failed to send comment');
    }
  };

  const handleDownload = (doc) => {
    const baseUrl = doc.doc_type === 'link' ? doc.url : `${process.env.REACT_APP_BACKEND_URL}${doc.file_path}`;
    
    // For PDFs and images, add download parameter to force download
    if (doc.doc_type !== 'link') {
      const url = `${baseUrl}?download=true`;
      window.location.href = url;
    } else {
      // For links, just open in new tab
      window.open(baseUrl, '_blank');
    }
  };

  const handleView = (doc) => {
    setViewingDoc(doc);
    setViewerOpen(true);
  };

  const handleEditDoc = (doc) => {
    setEditingDoc(doc);
    setEditDocData({
      title: doc.title,
      description: doc.description,
      category_id: doc.category_id
    });
    setEditDocOpen(true);
  };

  const handleUpdateDoc = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('title', editDocData.title);
      formData.append('description', editDocData.description);
      formData.append('category_id', editDocData.category_id);

      await axios.patch(`${API}/documents/${editingDoc.id}`, formData);
      toast.success('Document updated successfully');
      setEditDocOpen(false);
      setEditingDoc(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update document');
    }
  };

  const handleDeleteDoc = async () => {
    try {
      await axios.delete(`${API}/documents/${docToDelete.id}`);
      toast.success('Document deleted successfully');
      setDeleteDocOpen(false);
      setDocToDelete(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    // Validate passwords
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('current_password', passwordData.currentPassword);
      formData.append('new_password', passwordData.newPassword);
      
      await axios.post(`${API}/users/change-password`, formData);
      toast.success('Password changed successfully');
      setPasswordDialogOpen(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  const handleReplaceDoc = async (e) => {
    e.preventDefault();
    if (!replaceFile) {
      toast.error('Please select a file to upload');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('title', docToReplace.title);
      formData.append('description', docToReplace.description);
      formData.append('category_id', docToReplace.category_id);
      
      // Add the new file
      if (docToReplace.doc_type === 'link') {
        toast.error('Cannot replace file for link documents. Please edit the URL instead.');
        return;
      }
      formData.append('file', replaceFile);

      await axios.patch(`${API}/documents/${docToReplace.id}`, formData);
      toast.success('Document file replaced successfully');
      setReplaceDocOpen(false);
      setDocToReplace(null);
      setReplaceFile(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to replace document file');
    }
  };

  const handleCopyEmail = (email) => {
    navigator.clipboard.writeText(email).then(() => {
      toast.success('Email copied to clipboard!');
    }).catch(() => {
      toast.error('Failed to copy email');
    });
  };

  const handleCreateStaff = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/directory/staff`, staffData);
      toast.success(auth.user.role === 'admin' ? 'Staff member added' : 'Staff member submitted for approval');
      setStaffDialogOpen(false);
      setStaffData({ full_name: '', phone: '', email: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to add staff');
    }
  };

  const handleCreateTradesman = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/directory/tradesmen`, tradesmanData);
      toast.success(auth.user.role === 'admin' ? 'Tradesman added' : 'Tradesman submitted for approval');
      setTradesmanDialogOpen(false);
      setTradesmanData({ business_name: '', trade_type: '', contact_number: '', email: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to add tradesman');
    }
  };

  const handleCreateOtherContact = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/directory/other-contacts`, otherContactData);
      toast.success(auth.user.role === 'admin' ? 'Contact added' : 'Contact submitted for approval');
      setOtherContactDialogOpen(false);
      setOtherContactData({ business_name: '', what_they_do: '', name: '', contact_number: '', email: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to add contact');
    }
  };

  const getDocumentUrl = (doc) => {
    return doc.doc_type === 'link' ? doc.url : `${process.env.REACT_APP_BACKEND_URL}${doc.file_path}`;
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      fetchData();
      return;
    }
    try {
      const response = await axios.get(`${API}/documents/search?q=${searchQuery}`);
      setDocuments(response.data);
    } catch (error) {
      toast.error('Search failed');
    }
  };

  // Determine which documents to show based on showPrivate toggle
  const docsToShow = showPrivate ? privateDocuments : documents;
  
  const filteredDocs = selectedCategory === 'all'
    ? docsToShow
    : docsToShow.filter(doc => doc.category_id === selectedCategory);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E4E1DC]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#E4E1DC] via-white to-[#E4E1DC]">
      {/* Header */}
      <header className="bg-[#E4E1DC] border-b border-[#E4E1DC] shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img src="/avenue-logo-purple.svg" alt="Avenue" className="h-10 sm:h-9 w-auto" />
            </div>
            {/* Desktop buttons */}
            <div className="hidden md:flex items-center gap-3">
              {auth.user.role === 'admin' && (
                <>
                  <Button
                    onClick={() => navigate('/admin')}
                    className="bg-[#21123B] text-[#E4E1DC] border-0 hover:bg-[#21123B]/90 relative"
                    data-testid="admin-panel-button"
                  >
                    Admin Panel
                    {unreadCount > 0 && (
                      <span className="absolute -top-2 -right-2 bg-white text-[#21123B] text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                        {unreadCount}
                      </span>
                    )}
                  </Button>
                </>
              )}
              <Button
                onClick={() => setPasswordDialogOpen(true)}
                className="bg-[#21123B] text-[#E4E1DC] border-0 hover:bg-[#21123B]/90"
              >
                Change Password
              </Button>
              <Button
                onClick={auth.logout}
                className="bg-[#21123B] text-[#E4E1DC] border-0 hover:bg-[#21123B]/90"
                data-testid="logout-button"
              >
                Logout
              </Button>
            </div>
            {/* Mobile hamburger menu */}
            <div className="md:hidden">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    className="bg-[#21123B] text-[#E4E1DC] border-0 hover:bg-[#21123B]/90 p-2 h-10 w-10 relative"
                    data-testid="mobile-menu-button"
                    aria-label="Open menu"
                  >
                    <Menu className="h-5 w-5" />
                    {auth.user.role === 'admin' && unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-white text-[#21123B] text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                        {unreadCount}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-[#21123B] border-[#21123B] text-[#E4E1DC]">
                  {auth.user.role === 'admin' && (
                    <>
                      <DropdownMenuItem
                        onClick={() => navigate('/admin')}
                        className="text-[#E4E1DC] focus:bg-[#E4E1DC] focus:text-[#21123B] cursor-pointer flex justify-between"
                        data-testid="mobile-admin-panel-item"
                      >
                        Admin Panel
                        {unreadCount > 0 && (
                          <span className="bg-[#E4E1DC] text-[#21123B] text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                            {unreadCount}
                          </span>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-[#E4E1DC]/20" />
                    </>
                  )}
                  <DropdownMenuItem
                    onClick={() => setPasswordDialogOpen(true)}
                    className="text-[#E4E1DC] focus:bg-[#E4E1DC] focus:text-[#21123B] cursor-pointer"
                  >
                    Change Password
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-[#E4E1DC]/20" />
                  <DropdownMenuItem
                    onClick={auth.logout}
                    className="text-[#E4E1DC] focus:bg-[#E4E1DC] focus:text-[#21123B] cursor-pointer"
                    data-testid="mobile-logout-item"
                  >
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Upload Bar */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="flex-1 flex gap-2">
            <Input
              placeholder="Search documents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              className="flex-1"
              data-testid="search-input"
            />
            <Button onClick={handleSearch} style={{ backgroundColor: '#21123B', color: '#E4E1DC' }} data-testid="search-button">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </Button>
          </div>
          <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
            <DialogTrigger asChild>
              <Button style={{ backgroundColor: '#21123B', color: '#E4E1DC' }} className="whitespace-nowrap" data-testid="upload-button">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Upload Document
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md" data-testid="upload-dialog">
              <DialogHeader>
                <DialogTitle>Upload Document</DialogTitle>
                <DialogDescription>
                  {auth.user.role === 'employee' ? 'Your document will be submitted for admin approval' : 'Upload a new document'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleUpload} className="space-y-4">
                <div className="space-y-2">
                  <Label>Title</Label>
                  <Input
                    value={uploadData.title}
                    onChange={(e) => setUploadData({ ...uploadData, title: e.target.value })}
                    required
                    data-testid="upload-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={uploadData.description}
                    onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })}
                    data-testid="upload-description-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={uploadData.category_id} onValueChange={(val) => setUploadData({ ...uploadData, category_id: val })} required>
                    <SelectTrigger data-testid="upload-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Document Type</Label>
                  <Select value={uploadData.doc_type} onValueChange={(val) => setUploadData({ ...uploadData, doc_type: val })}>
                    <SelectTrigger data-testid="upload-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="link">Embeddable Link (YouTube, Google Docs)</SelectItem>
                      <SelectItem value="external_link">External Link (Drive Folder - Copy/Paste)</SelectItem>
                      <SelectItem value="pdf">PDF Document</SelectItem>
                      <SelectItem value="word">Word Document (.docx)</SelectItem>
                      <SelectItem value="excel">Excel Spreadsheet (.xlsx)</SelectItem>
                      <SelectItem value="image">Image</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {(uploadData.doc_type === 'link' || uploadData.doc_type === 'external_link') ? (
                  <div className="space-y-2">
                    <Label>URL</Label>
                    <Input
                      type="url"
                      value={uploadData.url}
                      onChange={(e) => setUploadData({ ...uploadData, url: e.target.value })}
                      placeholder={
                        uploadData.doc_type === 'external_link' 
                          ? "https://drive.google.com/... (folder or file link)"
                          : "https://... (YouTube, Google Doc, etc.)"
                      }
                      required
                      data-testid="upload-url-input"
                    />
                    {uploadData.doc_type === 'external_link' && (
                      <p className="text-xs text-gray-500 italic">
                        External links will show a copy button instead of embedding
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label>File</Label>
                    <Input
                      type="file"
                      accept={
                        uploadData.doc_type === 'pdf' ? '.pdf' : 
                        uploadData.doc_type === 'word' ? '.doc,.docx' : 
                        uploadData.doc_type === 'excel' ? '.xls,.xlsx' : 
                        'image/*'
                      }
                      onChange={(e) => setUploadData({ ...uploadData, file: e.target.files[0] })}
                      required
                      data-testid="upload-file-input"
                    />
                  </div>
                )}
                <div className="flex items-center space-x-2 p-4 bg-[#E4E1DC] border border-[#21123B]/20 rounded-lg">
                  <input
                    type="checkbox"
                    id="is_private"
                    checked={uploadData.is_private}
                    onChange={(e) => setUploadData({ ...uploadData, is_private: e.target.checked })}
                    className="w-4 h-4 accent-[#21123B] border-gray-300 rounded focus:ring-[#21123B]"
                  />
                  <div className="flex-1">
                    <Label htmlFor="is_private" className="text-sm font-medium text-[#21123B] cursor-pointer">
                      Make this a private document
                    </Label>
                    <p className="text-xs text-[#21123B]/70 mt-1">
                      Only you can see private documents. They don't need approval and won't be shared with others.
                    </p>
                  </div>
                </div>
                <Button type="submit" className="w-full" style={{ backgroundColor: '#21123B', color: '#E4E1DC' }} data-testid="upload-submit-button">
                  {uploadData.is_private ? 'Upload Private Document' : 'Upload'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {!showDirectory ? (
          <>
            {/* View Toggle Buttons */}
            <div className="flex gap-2 mb-4">
              <Button
                variant={!showPrivate ? 'default' : 'outline'}
                onClick={() => setShowPrivate(false)}
                className={!showPrivate ? 'bg-[#21123B] text-[#E4E1DC] hover:bg-[#21123B]/90' : ''}
                size="sm"
              >
                All Documents
              </Button>
              <Button
                variant={showPrivate ? 'default' : 'outline'}
                onClick={() => setShowPrivate(true)}
                className={showPrivate ? 'bg-[#21123B] text-white hover:bg-[#21123B]/90' : 'border-[#21123B]/40 text-[#21123B]'}
                size="sm"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                My Private Documents
              </Button>
            </div>
            
            {/* Category Filter */}
            <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
              <Button
                variant={selectedCategory === 'all' ? 'default' : 'outline'}
                onClick={() => setSelectedCategory('all')}
                className={selectedCategory === 'all' ? 'bg-[#21123B] text-[#E4E1DC] hover:bg-[#21123B]/90' : ''}
                data-testid="category-all"
              >
                All
              </Button>
              {categories.map(cat => (
                <Button
                  key={cat.id}
                  variant={selectedCategory === cat.id ? 'default' : 'outline'}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={selectedCategory === cat.id ? 'bg-[#21123B] text-[#E4E1DC] hover:bg-[#21123B]/90' : ''}
                  data-testid={`category-${cat.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {cat.name}
                </Button>
              ))}
            </div>

            {/* Documents Grid */}
            {filteredDocs.length === 0 ? (
              <div className="text-center py-16">
                <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-gray-500">No documents found</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredDocs.map(doc => (
                  <Card key={doc.id} className="card-hover border-0 shadow-md" data-testid={`document-${doc.id}`}>
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <CardTitle className="text-lg line-clamp-1" data-testid="document-title">{doc.title}</CardTitle>
                            {doc.is_private && (
                              <Badge className="bg-[#21123B]/10 text-[#21123B] text-xs">
                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                                Private
                              </Badge>
                            )}
                          </div>
                          <CardDescription className="line-clamp-2 mt-1" data-testid="document-description">{doc.description}</CardDescription>
                        </div>
                        <div className="flex-shrink-0">
                          {doc.doc_type === 'link' && (
                            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                              </svg>
                            </div>
                          )}
                          {doc.doc_type === 'external_link' && (
                            <div className="w-10 h-10 rounded-full bg-[#21123B]/10 flex items-center justify-center">
                              <svg className="w-5 h-5 text-[#21123B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                            </div>
                          )}
                          {doc.doc_type === 'pdf' && (
                            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                              <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                              </svg>
                            </div>
                          )}
                          {doc.doc_type === 'image' && (
                            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                          )}
                          {doc.doc_type === 'word' && (
                            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                              <svg className="w-5 h-5 text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </div>
                          )}
                          {doc.doc_type === 'excel' && (
                            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                              <svg className="w-5 h-5 text-green-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex items-center justify-between text-xs text-gray-600">
                        <Badge variant="secondary" className="bg-orange-100 text-orange-700" data-testid="document-category">{doc.category_name}</Badge>
                        {!doc.approved && (
                          <Badge variant="outline" className="border-yellow-400 text-yellow-700">Pending</Badge>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        <p>Uploaded by {doc.uploaded_by_name}</p>
                        <p>{new Date(doc.created_at).toLocaleDateString()}</p>
                      </div>
                      {auth.user.role === 'admin' && (
                        <div className="pt-2 space-y-2">
                          <Button
                            onClick={() => handleEditDoc(doc)}
                            variant="outline"
                            size="sm"
                            className="w-full"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Edit Details
                          </Button>
                          {doc.doc_type !== 'link' && (
                            <Button
                              onClick={() => {
                                setDocToReplace(doc);
                                setReplaceDocOpen(true);
                              }}
                              variant="outline"
                              size="sm"
                              className="w-full"
                            >
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                              </svg>
                              Replace File
                            </Button>
                          )}
                          <Button
                            onClick={() => {
                              setDocToDelete(doc);
                              setDeleteDocOpen(true);
                            }}
                            variant="destructive"
                            size="sm"
                            className="w-full"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Delete Document
                          </Button>
                        </div>
                      )}
                      

                      <div className="flex gap-2 pt-2">
                        {(doc.doc_type !== 'pdf' && doc.doc_type !== 'word' && doc.doc_type !== 'excel') && (
                          <Button
                            onClick={() => handleView(doc)}
                            className="flex-1"
                            style={{ backgroundColor: '#21123B', color: '#E4E1DC' }}
                            size="sm"
                            data-testid="document-view-button"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            View
                          </Button>
                        )}
                        <Button
                          onClick={() => handleDownload(doc)}
                          variant="outline"
                          className={doc.doc_type === 'pdf' || doc.doc_type === 'word' || doc.doc_type === 'excel' ? 'w-full' : 'flex-1'}
                          size="sm"
                          data-testid="document-download-button"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                          Download
                        </Button>
                      </div>
                      <div className="pt-2">
                        <Button
                          onClick={() => {
                            setSelectedDoc(doc);
                            setCommentOpen(true);
                          }}
                          variant="outline"
                          size="sm"
                          className="w-full"
                          data-testid="document-comment-button"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                          </svg>
                          Request Change
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        ) : (
          /* Directory View */
          <div className="space-y-6">
            <h2 className="text-2xl font-bold heading mb-6" style={{ color: '#2F323B' }}>Directory</h2>
            
            {/* Staff Section */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold" style={{ color: '#21123B' }}>Staff ({staff.length})</h3>
                <Dialog open={staffDialogOpen} onOpenChange={setStaffDialogOpen}>
                  <DialogTrigger asChild>
                    <Button style={{ backgroundColor: '#21123B', color: '#E4E1DC' }} size="sm">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add Staff
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add Staff Member</DialogTitle>
                      <DialogDescription>
                        {auth.user.role === 'employee' ? 'Your submission will be reviewed by admin' : 'Add a new staff member'}
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateStaff} className="space-y-4">
                      <div className="space-y-2">
                        <Label>Full Name</Label>
                        <Input value={staffData.full_name} onChange={(e) => setStaffData({ ...staffData, full_name: e.target.value })} required />
                      </div>
                      <div className="space-y-2">
                        <Label>Phone</Label>
                        <Input value={staffData.phone} onChange={(e) => setStaffData({ ...staffData, phone: e.target.value })} required />
                      </div>
                      <div className="space-y-2">
                        <Label>Email</Label>
                        <Input type="email" value={staffData.email} onChange={(e) => setStaffData({ ...staffData, email: e.target.value })} required />
                      </div>
                      <Button type="submit" className="w-full" style={{ backgroundColor: '#21123B', color: '#E4E1DC' }}>
                        {auth.user.role === 'employee' ? 'Submit for Approval' : 'Add Staff'}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
              {staff.length === 0 ? (
                <Card>
                  <CardContent className="pt-6 text-center text-gray-500">
                    No staff members added yet
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-3">
                  {staff.map(s => (
                    <Card key={s.id}>
                      <CardContent className="pt-4">
                        <h4 className="font-semibold text-lg">{s.full_name}</h4>
                        <div className="mt-2 space-y-2 text-sm">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-600">📞</span>
                            <a href={`tel:${s.phone}`} className="text-[#21123B] hover:underline font-medium">
                              {s.phone}
                            </a>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-600">📧</span>
                            <span className="text-gray-600 flex-1">{s.email}</span>
                            <Button
                              onClick={() => handleCopyEmail(s.email)}
                              variant="outline"
                              size="sm"
                              className="h-7 px-2"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Tradesmen Section */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold" style={{ color: '#21123B' }}>Tradesmen ({tradesmen.length})</h3>
                <Dialog open={tradesmanDialogOpen} onOpenChange={setTradesmanDialogOpen}>
                  <DialogTrigger asChild>
                    <Button style={{ backgroundColor: '#21123B', color: '#E4E1DC' }} size="sm">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add Tradesman
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add Tradesman</DialogTitle>
                      <DialogDescription>
                        {auth.user.role === 'employee' ? 'Your submission will be reviewed by admin' : 'Add a new tradesman'}
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateTradesman} className="space-y-4">
                      <div className="space-y-2">
                        <Label>Business Name</Label>
                        <Input value={tradesmanData.business_name} onChange={(e) => setTradesmanData({ ...tradesmanData, business_name: e.target.value })} required />
                      </div>
                      <div className="space-y-2">
                        <Label>Trade Type</Label>
                        <Input value={tradesmanData.trade_type} onChange={(e) => setTradesmanData({ ...tradesmanData, trade_type: e.target.value })} required />
                      </div>
                      <div className="space-y-2">
                        <Label>Contact Number</Label>
                        <Input value={tradesmanData.contact_number} onChange={(e) => setTradesmanData({ ...tradesmanData, contact_number: e.target.value })} required />
                      </div>
                      <div className="space-y-2">
                        <Label>Email</Label>
                        <Input type="email" value={tradesmanData.email} onChange={(e) => setTradesmanData({ ...tradesmanData, email: e.target.value })} required />
                      </div>
                      <Button type="submit" className="w-full" style={{ backgroundColor: '#21123B', color: '#E4E1DC' }}>
                        {auth.user.role === 'employee' ? 'Submit for Approval' : 'Add Tradesman'}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
              {tradesmen.length === 0 ? (
                <Card>
                  <CardContent className="pt-6 text-center text-gray-500">
                    No tradesmen added yet
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-3">
                  {tradesmen.map(t => (
                    <Card key={t.id}>
                      <CardContent className="pt-4">
                        <h4 className="font-semibold text-lg">{t.business_name}</h4>
                        <div className="mt-2 space-y-2 text-sm">
                          <p className="text-gray-600">🔧 Trade: {t.trade_type}</p>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-600">📞</span>
                            <a href={`tel:${t.contact_number}`} className="text-[#21123B] hover:underline font-medium">
                              {t.contact_number}
                            </a>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-600">📧</span>
                            <span className="text-gray-600 flex-1">{t.email}</span>
                            <Button
                              onClick={() => handleCopyEmail(t.email)}
                              variant="outline"
                              size="sm"
                              className="h-7 px-2"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Other Contacts Section */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold" style={{ color: '#21123B' }}>Other Contacts ({otherContacts.length})</h3>
                <Dialog open={otherContactDialogOpen} onOpenChange={setOtherContactDialogOpen}>
                  <DialogTrigger asChild>
                    <Button style={{ backgroundColor: '#21123B', color: '#E4E1DC' }} size="sm">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add Contact
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add Other Contact</DialogTitle>
                      <DialogDescription>
                        {auth.user.role === 'employee' ? 'Your submission will be reviewed by admin' : 'Add a new contact'}
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateOtherContact} className="space-y-4">
                      <div className="space-y-2">
                        <Label>Business Name</Label>
                        <Input value={otherContactData.business_name} onChange={(e) => setOtherContactData({ ...otherContactData, business_name: e.target.value })} required />
                      </div>
                      <div className="space-y-2">
                        <Label>What They Do</Label>
                        <Input value={otherContactData.what_they_do} onChange={(e) => setOtherContactData({ ...otherContactData, what_they_do: e.target.value })} required />
                      </div>
                      <div className="space-y-2">
                        <Label>Contact Name</Label>
                        <Input value={otherContactData.name} onChange={(e) => setOtherContactData({ ...otherContactData, name: e.target.value })} required />
                      </div>
                      <div className="space-y-2">
                        <Label>Contact Number</Label>
                        <Input value={otherContactData.contact_number} onChange={(e) => setOtherContactData({ ...otherContactData, contact_number: e.target.value })} required />
                      </div>
                      <div className="space-y-2">
                        <Label>Email</Label>
                        <Input type="email" value={otherContactData.email} onChange={(e) => setOtherContactData({ ...otherContactData, email: e.target.value })} required />
                      </div>
                      <Button type="submit" className="w-full" style={{ backgroundColor: '#21123B', color: '#E4E1DC' }}>
                        {auth.user.role === 'employee' ? 'Submit for Approval' : 'Add Contact'}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
              {otherContacts.length === 0 ? (
                <Card>
                  <CardContent className="pt-6 text-center text-gray-500">
                    No other contacts added yet
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-3">
                  {otherContacts.map(c => (
                    <Card key={c.id}>
                      <CardContent className="pt-4">
                        <h4 className="font-semibold text-lg">{c.business_name}</h4>
                        <div className="mt-2 space-y-2 text-sm">
                          <p className="text-gray-600">💼 {c.what_they_do}</p>
                          <p className="text-gray-600">👤 Contact: {c.name}</p>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-600">📞</span>
                            <a href={`tel:${c.contact_number}`} className="text-[#21123B] hover:underline font-medium">
                              {c.contact_number}
                            </a>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-600">📧</span>
                            <span className="text-gray-600 flex-1">{c.email}</span>
                            <Button
                              onClick={() => handleCopyEmail(c.email)}
                              variant="outline"
                              size="sm"
                              className="h-7 px-2"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Comment Dialog */}
      <Dialog open={commentOpen} onOpenChange={setCommentOpen}>
        <DialogContent data-testid="comment-dialog">
          <DialogHeader>
            <DialogTitle>Request Change</DialogTitle>
            <DialogDescription>
              Send a message to admin about this document
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleComment} className="space-y-4">
            <div className="space-y-2">
              <Label>Message</Label>
              <Textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Describe what changes you'd like..."
                required
                rows={4}
                data-testid="comment-input"
              />
            </div>
            <Button type="submit" className="w-full" style={{ backgroundColor: '#21123B', color: '#E4E1DC' }} data-testid="comment-submit-button">
              Send to Admin
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Document Dialog */}
      <Dialog open={editDocOpen} onOpenChange={setEditDocOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Document</DialogTitle>
            <DialogDescription>
              Update document details
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateDoc} className="space-y-4">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={editDocData.title}
                onChange={(e) => setEditDocData({ ...editDocData, title: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={editDocData.description}
                onChange={(e) => setEditDocData({ ...editDocData, description: e.target.value })}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select
                value={editDocData.category_id}
                onValueChange={(value) => setEditDocData({ ...editDocData, category_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" className="w-full" style={{ backgroundColor: '#21123B', color: '#E4E1DC' }}>
              Update Document
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Document Confirmation Dialog */}
      <Dialog open={deleteDocOpen} onOpenChange={setDeleteDocOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Document</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{docToDelete?.title}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 justify-end pt-4">
            <Button
              onClick={() => setDeleteDocOpen(false)}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteDoc}
              variant="destructive"
            >
              Delete Document
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Replace Document File Dialog */}
      <Dialog open={replaceDocOpen} onOpenChange={setReplaceDocOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Replace Document File</DialogTitle>
            <DialogDescription>
              Upload a new file to replace the current file for "{docToReplace?.title}"
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleReplaceDoc} className="space-y-4">
            <div className="space-y-2">
              <Label>New File</Label>
              <Input
                type="file"
                onChange={(e) => setReplaceFile(e.target.files[0])}
                accept={
                  docToReplace?.doc_type === 'pdf' ? '.pdf' : 
                  docToReplace?.doc_type === 'word' ? '.doc,.docx' : 
                  docToReplace?.doc_type === 'excel' ? '.xls,.xlsx' : 
                  docToReplace?.doc_type === 'image' ? 'image/*' : 
                  '*'
                }
                required
              />
              <p className="text-xs text-gray-500">
                Current file type: {
                  docToReplace?.doc_type === 'pdf' ? 'PDF' : 
                  docToReplace?.doc_type === 'word' ? 'Word Document' : 
                  docToReplace?.doc_type === 'excel' ? 'Excel Spreadsheet' : 
                  docToReplace?.doc_type === 'image' ? 'Image' : 
                  'Unknown'
                }
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                type="button"
                onClick={() => {
                  setReplaceDocOpen(false);
                  setReplaceFile(null);
                }}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1"
                style={{ backgroundColor: '#21123B', color: '#E4E1DC' }}
              >
                Replace File
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Document Viewer Dialog */}
      <Dialog open={viewerOpen} onOpenChange={setViewerOpen}>
        <DialogContent className="max-w-5xl h-[85vh]" data-testid="document-viewer-dialog">
          <DialogHeader>
            <div className="flex items-center justify-between pr-8">
              <div>
                <DialogTitle className="text-xl">{viewingDoc?.title}</DialogTitle>
                <DialogDescription>{viewingDoc?.description}</DialogDescription>
              </div>
              <Button
                onClick={() => handleDownload(viewingDoc)}
                size="sm"
                style={{ backgroundColor: '#21123B', color: '#E4E1DC' }}
                data-testid="viewer-download-button"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download
              </Button>
            </div>
          </DialogHeader>
          <div className="flex-1 overflow-auto bg-gray-50 rounded-lg border">
            {viewingDoc && (
              <>
                {viewingDoc.doc_type === 'pdf' && (
                  <div className="flex flex-col items-center justify-center p-8 h-full space-y-4">
                    <svg className="w-16 h-16 text-[#21123B]/30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    <div className="text-center space-y-2">
                      <h3 className="text-lg font-semibold">PDF Document</h3>
                      <p className="text-sm text-gray-500">Click below to download and open this PDF</p>
                      <Button
                        onClick={() => handleDownload(viewingDoc)}
                        style={{ backgroundColor: '#21123B', color: '#E4E1DC' }}
                        className="mt-4"
                        data-testid="download-pdf-button"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        Download PDF
                      </Button>
                    </div>
                  </div>
                )}
                {viewingDoc.doc_type === 'image' && (
                  <div className="flex items-center justify-center p-8 h-full">
                    <img
                      src={getDocumentUrl(viewingDoc)}
                      alt={viewingDoc.title}
                      className="max-w-full max-h-full object-contain"
                      data-testid="image-viewer"
                    />
                  </div>
                )}
                {viewingDoc.doc_type === 'link' && (
                  <iframe
                    src={viewingDoc.url}
                    className="w-full h-full min-h-[70vh]"
                    title={viewingDoc.title}
                    sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                    data-testid="link-viewer"
                  />
                )}
                {(viewingDoc.doc_type === 'external_link' || viewingDoc.doc_type === 'word' || viewingDoc.doc_type === 'excel') && (
                  <div className="flex flex-col items-center justify-center p-8 h-full space-y-4">
                    <svg className="w-16 h-16 text-[#21123B]/30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      {viewingDoc.doc_type === 'external_link' ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      )}
                    </svg>
                    <div className="text-center space-y-2">
                      <h3 className="text-lg font-semibold">
                        {viewingDoc.doc_type === 'external_link' ? 'External Link' :
                         viewingDoc.doc_type === 'word' ? 'Word Document' : 'Excel Spreadsheet'}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {viewingDoc.doc_type === 'external_link' 
                          ? 'Copy link and paste in a new browser tab'
                          : 'Click below to download and open this file'}
                      </p>
                      {viewingDoc.doc_type === 'external_link' && (
                        <p className="text-gray-600 max-w-md break-all text-sm">{viewingDoc.url}</p>
                      )}
                      {viewingDoc.doc_type === 'external_link' ? (
                        <div className="flex flex-col gap-2 mt-4">
                          <Button
                            onClick={() => {
                              navigator.clipboard.writeText(viewingDoc.url);
                              toast.success('Link copied! Open a new tab and paste it.');
                            }}
                            style={{ backgroundColor: '#21123B', color: '#E4E1DC' }}
                            data-testid="copy-link-button"
                          >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                            Copy Link
                          </Button>
                          <p className="text-xs text-gray-500 italic">
                            Due to security restrictions, please copy the link and paste it in a new browser tab
                          </p>
                        </div>
                      ) : (
                        <Button
                          onClick={() => handleDownload(viewingDoc)}
                          style={{ backgroundColor: '#21123B', color: '#E4E1DC' }}
                          className="mt-4"
                          data-testid="download-button"
                        >
                          Download & Open
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>
              Enter your current password and choose a new one
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="currentPassword">Current Password</Label>
              <Input
                id="currentPassword"
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                required
                placeholder="Enter current password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <Input
                id="newPassword"
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                required
                placeholder="At least 6 characters"
                minLength={6}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                required
                placeholder="Re-enter new password"
                minLength={6}
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => {
                setPasswordDialogOpen(false);
                setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
              }}>
                Cancel
              </Button>
              <Button type="submit" className="bg-[#21123B] text-[#E4E1DC] hover:bg-[#21123B]/90">
                Change Password
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
